import BackArrow from "../../../Assets/Icons/BackArrow";
import { Checkbox } from "primereact/checkbox";
import "./schedulesession.css";
import { useState, useEffect } from "react";
import { useBookSession } from "../../../APIContext/BookSessionContext";
import { usePlayerAccount } from "../../../APIContext/PlayerAccountContext";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import SessionScheduled from "../SessionScheduled";
import StaticPageHeaders from "../../../Components/StaticPageHeaders";
import { useRef } from "react";
import EventFilterModal from "../../../Components/EventFilterModal/EventFilterModal";

const ScheduleYourSessions = () => {
  const playerChangedRef = useRef(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const sport_id = query.get("sport_id");
  const sale_id = query.get("sale_id");
  const is_unlimited = query.get("is_unlimited");

  const [eventData, setEventData] = useState([]);
  const [playersData, setPlayersData] = useState([]);

  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [showModalFilter, setShowModalFilter] = useState(false);
  const [note, setNote] = useState("");
  const [filterData, setFilterData] = useState({
    location: "",
    date: null,
    start_time: null,
    end_time: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const { fetchEventsDetails, checkEventsBooking } = useBookSession();
  const { fetchParentPlayers } = usePlayerAccount();

  // ------------------------------
  // FORMATTERS
  // ------------------------------
  const formatDay = (utcString) => {
    return new Date(utcString).toLocaleDateString("en-US", { weekday: "long" });
  };

  const formatDate = (utcString) => {
    return new Date(utcString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (utcString) => {
    return new Date(utcString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // ------------------------------
  // FETCH DATA
  // ------------------------------
  useEffect(() => {
    const fetchData = async () => {
      if (!sport_id || !sale_id || !is_unlimited) return;

      const sessionData = await fetchEventsDetails(
        sport_id,
        sale_id,
        selectedPlayer,
        filterData,
      );
      const players = await fetchParentPlayers(sale_id);

      if (players) setPlayersData(players);

      if (sessionData?.events) {
        setEventData(sessionData.events);

        if (playerChangedRef.current && sessionData.events.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "No Events Found",
            text: "No sessions available for the selected player.",
            confirmButtonColor: "#f7931a",
            cancelButtonColor: "#1C1C1C",
          });
        }
      }
    };

    fetchData();
  }, [selectedPlayer, showModalFilter]);

  // ------------------------------
  // HANDLE CHECKBOX SELECT
  // ------------------------------
  const toggleEventSelection = (eventId) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId],
    );
  };

  // ------------------------------
  // HANDLE SUBMIT
  // ------------------------------
  const handleSubmit = async () => {
    if (!selectedPlayer) {
      Swal.fire({
        icon: "warning",
        title: "Select a Player",
        text: "Please select a player before proceeding.",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });
      return;
    }

    if (selectedEvents.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Select Sessions",
        text: "Please select at least one session.",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });
      return;
    }

    const payload = {
      event_ids: selectedEvents,
      sale_id: Number(sale_id),
      player_id: selectedPlayer ?? null,
      notes: note,
    };

    // console.log(payload);

    try {
      const res = await checkEventsBooking(payload);
      if (res) {
        let confirmedCount = res.data?.confirmed || 0;
        let skippedCount = res.data?.skipped || 0;

        if (confirmedCount.length === 0 && skippedCount.length > 0) {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text:
              res.data?.skipped.map(
                (error) => `${error.reason} Event name: ${error.event_name}`,
              ) || "Unable to proceed with booking.",
            confirmButtonColor: "#f7931a",
            cancelButtonColor: "#1C1C1C",
          });
        } else {
          setDataB(res.data);
          navigate(`/session-scheduled`, {
            state: { data: res.data },
          });
        }
      }
    } catch (err) {
      // Swal.fire({
      //     icon: "error",
      //     title: "Error",
      //     text:  err.response.data.message ,
      // });
    }
  };

  return (
    <>
      <StaticPageHeaders title="Schedule Your Sessions" />
      <div
        style={{ filter: "brightness(1.28)" }}
        className="w-h-full d-flex justify-content-center bg-custom"
      >
        <div className="schedule-your-sessions-page">
          {/* Header */}
          <div className="schedule-header">
            <span
              onClick={() => navigate("/your-packages")}
              style={{ cursor: "pointer", filter: "brightness(0.7)" }}
            >
              <BackArrow />
            </span>
            <h2 className="text-clip">
              Choose Your Preferred Location, Date & Time
            </h2>
          </div>

          <div className="gi-sessions">
            {/* PLAYER SELECT — ONLY FOR LIMITED PACKAGES */}

            <div className="gi-session-proceed-btn">
              <div className="players-container select-wrap">
                <span className="text-clip">Select Your Player</span>
                <select
                  className="players-drop-down"
                  onChange={(e) => {
                    setFilterData(null);
                    playerChangedRef.current = true; // ✅ mark that user changed player
                    const value = e.target.value;
                    setSelectedPlayer(value ? Number(value) : null);
                  }}
                >
                  <option value="">Select Player</option>
                  {playersData.map((player) => (
                    <option key={player.id} value={player.id}>
                      {player.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="gi-field notes">
                <label className="text-clip">Notes (Optional)</label>
                <textarea
                  placeholder="Add a note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            {/* HEADER */}
            <div className="gi-sessions-header">
              <h4 className="text-clip">Instructions</h4>
              <button
                className="filter-btn"
                onClick={() => {
                  setShowModalFilter(true);
                }}
              >
                Filter <i className="ri-filter-2-line"></i>
              </button>
            </div>

            {/* SESSIONS LIST */}
            <div className="gi-sessions-list">
              {eventData.length === 0 && (
                <p className="no-sessions-message text-clip">
                  No sessions available. Please check back later.
                </p>
              )}

              {eventData.map((item) => (
                <div key={item.id} className="gi-session-item">
                  <div className="gi-session-item-left">
                    <Checkbox
                      onChange={() => toggleEventSelection(item.id)}
                      checked={selectedEvents.includes(item.id)}
                    />
                    <p className="text-clip">
                      {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                      {", "}
                      {item.event_type.charAt(0).toUpperCase() +
                        item.event_type.slice(1)}
                    </p>
                  </div>
                  <div className="gi-session-item-right">
                    <span className="gi-session-item-day text-clip">{item.location}</span>
                    <span className="gi-session-item-day text-clip">
                      {formatDay(item.start_datetime_utc)}
                    </span>
                    <span className="gi-session-item-date text-clip">
                      {formatDate(item.start_datetime_utc)}
                    </span>
                    <span className="gi-session-item-time text-clip">
                      {formatTime(item.start_datetime_utc)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* PROCEED BUTTON */}
            <div className="gi-session-proceed-btn">
              <button
                className="btn-primary btn-lg btn"
                onClick={() => handleSubmit()}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModalFilter && (
        <EventFilterModal
          // onClose={() => setShowModalFilter(false)}
          // onApply={(data) => {
          //     setFilterData(data);
          //     console.log("Applied Filter:", data)
          // }}

          filterData={filterData}
          setFilterData={setFilterData}
          onClose={() => setShowModalFilter(false)}
          onApply={() => {
            setShowModalFilter(false);
          }}
        />
      )}
    </>
  );
};

export default ScheduleYourSessions;
